// src/pages/Home.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { teamMembers, teamRoles } from '../data/translations';
import { BACKGROUNDS } from '../utils/bgStyle';
import { sendMessage } from '../services/api';

// Animated counter hook
function useCounter(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isPlus = String(target).includes('+');
    const num = parseInt(String(target).replace('+', ''));
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * num));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration, start]);
  return count;
}

function StatCard({ target, label }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const isPlus = String(target).includes('+');
  const count = useCounter(target, 2000, started);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setStarted(true); obs.disconnect(); } }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="stat-card fade-in-up" ref={ref}>
      <div className="stat-number">{count}{isPlus ? '+' : ''}</div>
      <p className="stat-label">{label}</p>
    </div>
  );
}

function TeamCard({ member, lang }) {
  const [imgError, setImgError] = useState(false);
  const initials = member.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const role = teamRoles[member.role]?.[lang] || teamRoles[member.role]?.en || member.role;

  return (
    <div className="team-card fade-in-up">
      {imgError ? (
        <div className="team-avatar-fallback">{initials}</div>
      ) : (
        <div className="team-avatar">
          <img src={member.image} alt={member.alt} onError={() => setImgError(true)} />
        </div>
      )}
      <h4>{member.name}</h4>
      <p className="team-role">{role}</p>
    </div>
  );
}

export default function Home({ onDonate }) {
  const { t, lang } = useLang();
  const [toast, setToast] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');

  const handleContact = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError('');
    try {
      await sendMessage({ name: contactForm.name, email: contactForm.email, content: contactForm.message });
      setToast(lang === 'fr' ? 'Message envoyé ! Nous vous répondrons bientôt.' : 'Message sent! We will get back to you soon.');
      setContactForm({ name: '', email: '', message: '' });
      setTimeout(() => setToast(''), 5000);
    } catch (err) {
      setContactError(lang === 'fr' ? 'Erreur lors de l\'envoi. Réessayez.' : 'Failed to send. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <>
      {/* ── Hero ── */}
      <section id="home" className="hero" style={BACKGROUNDS.hero}>
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content fade-in-up">
              <p className="hero-assoc-name">Women And Child Empowerment Association</p>
              <h1>
                <span>{t('hero.title.part1')}</span><br />
                <span className="highlight">{t('hero.title.part2')}</span><br />
                <span>{t('hero.title.part3')}</span>
              </h1>
              <p>{t('hero.subtitle')}</p>
              <div className="hero-buttons">
                <a href="#contact" className="btn-primary" onClick={e => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  <span>{t('hero.buttons.join')}</span>
                  <i className="fas fa-arrow-right"></i>
                </a>
                <a href="#mission" className="btn-outline" onClick={e => { e.preventDefault(); document.querySelector('#mission')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  <i className="fas fa-heart"></i>
                  <span>{t('hero.buttons.learn')}</span>
                </a>
              </div>
            </div>
            <div className="hero-visual fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="hero-card">
                <div className="hero-icon"><i className="fas fa-users"></i></div>
                <div className="hero-number">1000+</div>
                <p className="hero-label">{t('hero.stats.lives')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className="bg-muted" style={BACKGROUNDS.about}>
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">{t('about.label')}</span>
            <h2><span className="highlight">{t('about.title')}</span></h2>
            <p>{t('about.description')}</p>
          </div>
          <div className="stats-grid">
            <StatCard target="12" label={t('about.stats.members')} />
            <StatCard target="1000+" label={t('about.stats.impact')} />
            <StatCard target="6" label={t('about.stats.objectives')} />
          </div>
        </div>
      </section>

      {/* ── Mission ── */}
      <section id="mission" style={BACKGROUNDS.mission}>
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">{t('mission.label')}</span>
            <h2><span className="highlight">{t('mission.title')}</span></h2>
            <p>{t('mission.description')}</p>
          </div>
          <div className="grid grid-3">
            {[
              { icon: 'fa-graduation-cap', key: 'education' },
              { icon: 'fa-star', key: 'girls' },
              { icon: 'fa-balance-scale', key: 'equality' },
              { icon: 'fa-shield-alt', key: 'protection' },
              { icon: 'fa-heartbeat', key: 'health' },
              { icon: 'fa-users-cog', key: 'leadership' },
            ].map(({ icon, key }, i) => (
              <div key={key} className="card fade-in-up" style={{ animationDelay: `${0.1 * (i + 1)}s` }}>
                <div className="card-icon"><i className={`fas ${icon}`}></i></div>
                <h4>{t(`mission.objectives.${key}.title`)}</h4>
                <p>{t(`mission.objectives.${key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Vision & Values ── */}
      <section className="bg-card">
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">{t('vision.label')}</span>
            <h2><span className="highlight">{t('vision.title')}</span></h2>
          </div>
          <div className="grid grid-2">
            <div className="feature-card fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h3><i className="fas fa-eye"></i><span>{t('vision.vision.heading')}</span></h3>
              <p>{t('vision.vision.text')}</p>
            </div>
            <div className="feature-card fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h3><i className="fas fa-award"></i><span>{t('vision.values.heading')}</span></h3>
              <p>{t('vision.values.intro')}</p>
              <div className="value-tags">
                {Object.values(t('vision.values.list') || {}).map((v, i) => (
                  <span key={i} className="value-tag">{v}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Child Protection ── */}
      <section style={BACKGROUNDS.protection}>
        <div className="container" style={{ maxWidth: 900 }}>
          <div className="card text-center fade-in-up" style={{ textAlign: 'center', padding: '4rem 3rem' }}>
            <div style={{ width: 100, height: 100, margin: '0 auto 2rem', background: 'linear-gradient(135deg,var(--primary),var(--secondary))', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-shield-alt" style={{ fontSize: '3rem', color: 'white' }}></i>
            </div>
            <h2 className="highlight" style={{ fontSize: '2.5rem', marginBottom: '1.5rem' }}>{t('childprotection.title')}</h2>
            <p style={{ fontSize: '1.125rem', color: 'rgba(26,26,26,0.7)', lineHeight: 1.8, marginBottom: '2rem' }}>{t('childprotection.description')}</p>
            <div className="value-tags" style={{ justifyContent: 'center' }}>
              {Object.values(t('childprotection.principles') || {}).map((p, i) => (
                <span key={i} className="value-tag">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Team ── */}
      <section id="team" className="bg-muted">
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">{t('team.label')}</span>
            <h2><span className="highlight">{t('team.title')}</span></h2>
            <p>{t('team.description')}</p>
          </div>
          <div className="grid grid-4">
            {teamMembers.map((m, i) => <TeamCard key={i} member={m} lang={lang} />)}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={BACKGROUNDS.cta}>
        <div className="container">
          <div className="cta-section fade-in-up">
            <h2>{t('cta.title')}</h2>
            <p>{t('cta.description')}</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://wa.me/237699065019" target="_blank" rel="noopener noreferrer" className="btn-white">
                <i className="fas fa-user"></i>
                <span>{t('cta.buttons.individual')}</span>
              </a>
              <a href="https://wa.me/237699065019" target="_blank" rel="noopener noreferrer" className="btn-white">
                <i className="fas fa-building"></i>
                <span>{t('cta.buttons.organization')}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Media ── */}
      <section id="media" className="bg-card">
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">{t('media.label')}</span>
            <h2><span className="highlight">{t('media.title')}</span></h2>
          </div>
          <div className="grid grid-3">
            {[
              { icon: 'fa-images',    titleKey: 'media.gallery.title',   descKey: 'media.gallery.description',   btnKey: 'media.gallery.button',   to: '/gallery' },
              { icon: 'fa-newspaper', titleKey: 'media.news.title',      descKey: 'media.news.description',      btnKey: 'media.news.button',       to: '/news' },
              { icon: 'fa-download',  titleKey: 'media.downloads.title', descKey: 'media.downloads.description', btnKey: 'media.downloads.button',  to: null },
            ].map(({ icon, titleKey, descKey, btnKey, to }, i) => (
              <div key={i} className="card fade-in-up" style={{ textAlign: 'center', animationDelay: `${0.1 * (i + 1)}s` }}>
                <div style={{ marginBottom: '2rem' }}><i className={`fas ${icon}`} style={{ fontSize: '4rem', color: 'var(--primary)' }}></i></div>
                <h3 style={{ marginBottom: '1rem' }}>{t(titleKey)}</h3>
                <p style={{ marginBottom: '1.5rem', color: 'rgba(26,26,26,0.7)' }}>{t(descKey)}</p>
                {to
                  ? <Link to={to} className="btn-outline">{t(btnKey)}</Link>
                  : <button className="btn-outline" onClick={() => alert('Coming soon!')}>{t(btnKey)}</button>
                }
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Donate Section ── */}
      <section>
        <div className="container" style={{ maxWidth: 900 }}>
          <div className="card fade-in-up" style={{ textAlign: 'center', padding: '4rem 3rem', background: 'linear-gradient(135deg,rgba(255,183,0,0.1),rgba(255,149,0,0.1))' }}>
            <div style={{ width: 100, height: 100, margin: '0 auto 2rem', background: 'linear-gradient(135deg,var(--primary),var(--secondary))', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-hand-holding-heart" style={{ fontSize: '3rem', color: 'white' }}></i>
            </div>
            <h2 className="highlight" style={{ fontSize: '2.5rem', marginBottom: '1.5rem' }}>{t('donate.title')}</h2>
            <p style={{ fontSize: '1.125rem', color: 'rgba(26,26,26,0.7)', lineHeight: 1.8, marginBottom: '1.5rem' }}>{t('donate.description')}</p>
            <p style={{ fontSize: '1rem', color: 'var(--primary)', fontWeight: 700, marginBottom: '2rem' }}>{t('donate.impact')}</p>
            <button className="btn-primary" style={{ fontSize: '1.125rem', padding: '16px 36px' }} onClick={onDonate}>
              <i className="fas fa-heart"></i>
              <span>{t('donate.button')}</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section id="contact" className="bg-muted" style={BACKGROUNDS.contact}>
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">{t('contact.label')}</span>
            <h2><span className="highlight">{t('contact.title')}</span></h2>
            <p>{t('contact.description')}</p>
          </div>
          <div className="grid grid-2">
            <div className="card fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h3 style={{ marginBottom: '2rem' }}>{t('contact.form.title')}</h3>
              <form onSubmit={handleContact}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <input type="text" className="form-control" placeholder={t('contact.form.name')} value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <input type="email" className="form-control" placeholder={t('contact.form.email')} value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} required />
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <textarea className="form-control" placeholder={t('contact.form.message')} value={contactForm.message} onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))} required />
                </div>
                {contactError && <p style={{ color: '#e53e3e', marginBottom: '1rem', fontSize: '0.9rem' }}>{contactError}</p>}
                <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={contactLoading}>
                  <i className={`fas ${contactLoading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                  <span>{contactLoading ? (lang === 'fr' ? 'Envoi...' : 'Sending...') : t('contact.form.submit')}</span>
                </button>
              </form>
            </div>

            <div className="fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="contact-info-card">
                <h4 style={{ marginBottom: '2rem' }}>{t('contact.info.title')}</h4>
                {[
                  { icon: 'fa-map-marker-alt', title: t('contact.info.address.label'), lines: [t('contact.info.address.value')] },
                  { icon: 'fa-phone', title: t('contact.info.phone.label'), lines: ['+237 682 292 807', '+237 640 487 860', '+237 699 065 019'] },
                  { icon: 'fa-envelope', title: 'Email', lines: ['waceassociation@gmail.com'] },
                ].map(({ icon, title, lines }, i) => (
                  <div key={i} className="contact-info-item" style={i === 2 ? { marginBottom: 0 } : {}}>
                    <div className="contact-icon"><i className={`fas ${icon}`}></i></div>
                    <div>
                      <h5>{title}</h5>
                      {lines.map((l, j) => <p key={j} style={{ color: 'rgba(26,26,26,0.7)' }}>{l}</p>)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="contact-info-card">
                <h4 style={{ marginBottom: '1.5rem' }}>{t('contact.social.title')}</h4>
                <div className="social-links">
                  {[['fab fa-facebook-f', 'Facebook'], ['fab fa-instagram', 'Instagram'], ['fab fa-linkedin-in', 'LinkedIn'], ['fab fa-twitter', 'Twitter']].map(([cls, label]) => (
                    <a key={label} href="#" className="social-link" aria-label={label}><i className={cls}></i></a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {toast && <div className="toast-message">{toast}</div>}
    </>
  );
}
